import React, { useState, useEffect, useRef } from 'react';
import {
  Row, Col, Card, Form, Input, Select, Button, Table, Tag, Typography,
  Space, Divider, message, Badge, Steps, Alert, Statistic, Switch
} from 'antd';
import {
  MobileOutlined, ScanOutlined, WifiOutlined, CheckCircleOutlined,
  LoginOutlined, LogoutOutlined, CloudSyncOutlined, UserOutlined,
  PlayCircleOutlined, StopOutlined, ReloadOutlined
} from '@ant-design/icons';
import { getHandheldEvents, getHandheldLocations, handheldScan, getHandheldScans, syncHandheldData, login as apiLogin } from '../api';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

export default function HandheldSimulator() {
  const [step, setStep]               = useState(0);
  const [loggedIn, setLoggedIn]       = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const [events, setEvents]           = useState([]);
  const [locations, setLocations]     = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [tagInput, setTagInput]       = useState('');
  const [scanLog, setScanLog]         = useState([]);
  const [scanning, setScanning]       = useState(false);
  const [autoScan, setAutoScan]       = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [syncDone, setSyncDone]       = useState(false);
  const [loginForm] = Form.useForm();
  const autoRef = useRef(null);
  const tagRef  = useRef(null);
  const performScanRef = useRef(null); // avoids stale closure in auto-scan interval

  const DEMO_TAGS = [
    'E280689400004016B8A7DE61','E280689400004016B8A7DE62','E280689400004016B8A7DE63',
    'E280689400004016B8A7DE64','E280689400004016B8A7DE65','E280689400004016B8A7DE66',
    'E280689400004016B8A7DE67','E280689400004016B8A7DE68','E280689400004016B8A7DE69',
    'E280689400004016B8A7DE70','E280689400004016B8A7DE71','E280689400004016B8A7DE72',
  ];

  const handleLogin = async () => {
    try {
      const values = await loginForm.validateFields();
      // Actually authenticate against the backend with the entered credentials
      const res = await apiLogin({ username: values.username, password: values.password });
      const data = res.data;
      if (!data?.success || !data?.token) {
        message.error(data?.message || 'Invalid credentials');
        return;
      }
      // Store token & user in same keys the rest of the app uses
      localStorage.setItem('rfid_token', data.token);
      localStorage.setItem('rfid_user', JSON.stringify(data.user));
      setSessionUser(data.user);
      setLoggedIn(true);
      setStep(1);
      const r = await getHandheldEvents();
      setEvents(r.data.data || []);
      message.success(`Welcome, ${data.user?.full_name || 'Operator'}!`);
    } catch (e) {
      if (e?.errorFields) return; // form validation failure already shown
      message.error(e?.response?.data?.message || 'Login failed');
    }
  };

  const handleSelectEvent = async () => {
    if (!selectedEvent) { message.warning('Select an event'); return; }
    if (!selectedLocation) { message.warning('Select a location'); return; }
    setStep(2);
    message.success('Ready to scan!');
    setTimeout(() => tagRef.current?.focus(), 200);
  };

  const loadLocations = async (eid) => {
    setSelectedEvent(eid);
    setSelectedLocation(null);
    try {
      const r = await getHandheldLocations(eid);
      setLocations(r.data.data || []);
    } catch (e) {}
  };

  const performScan = async (tag) => {
    const t = tag?.trim();
    if (!t) return;
    setScanning(true);
    try {
      const r = await handheldScan({ rfid_tags: [t], event_id: selectedEvent, location_id: selectedLocation, reader_name: 'Handheld-Simulator' });
      const result = r.data.results?.[0];
      const entry = {
        key: Date.now(),
        rfid_tag: t,
        delegate_name: result?.delegate_name || 'Unknown',
        status: result?.status || 'unknown',
        location: locations.find(l => l.id === selectedLocation)?.location_name || '-',
        time: dayjs().format('HH:mm:ss'),
      };
      setScanLog(prev => [entry, ...prev].slice(0, 200));
      setTagInput('');
    } catch (e) { message.error('Scan failed'); }
    finally { setScanning(false); }
  };

  // keep performScanRef pointing at the latest closure (with current event/location/locations)
  useEffect(() => { performScanRef.current = performScan; });

  useEffect(() => {
    if (autoRef.current) { clearInterval(autoRef.current); autoRef.current = null; }
    if (autoScan && step === 2) {
      autoRef.current = setInterval(() => {
        const tag = DEMO_TAGS[Math.floor(Math.random() * DEMO_TAGS.length)];
        performScanRef.current && performScanRef.current(tag);
      }, 1500);
    }
    return () => { if (autoRef.current) { clearInterval(autoRef.current); autoRef.current = null; } };
  }, [autoScan, step]); // eslint-disable-line

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncHandheldData({ scan_count: scanLog.length, event_id: selectedEvent });
      message.success(`${scanLog.length} scans synced to cloud`);
      setSyncDone(true);
    } catch (e) { message.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleLogout = () => {
    setStep(0); setLoggedIn(false); setSessionUser(null);
    setSelectedEvent(null); setSelectedLocation(null);
    setScanLog([]); setAutoScan(false); setSyncDone(false);
  };

  const scanColumns = [
    { title: 'Time', dataIndex: 'time', width: 80 },
    { title: 'RFID Tag', dataIndex: 'rfid_tag', render: v => <Text code style={{ fontSize: 10 }}>{v.slice(-12)}</Text>, width: 110 },
    { title: 'Delegate', dataIndex: 'delegate_name', render: v => <Text strong>{v}</Text> },
    { title: 'Location', dataIndex: 'location' },
    { title: 'Status', dataIndex: 'status', render: v => <Tag color={v === 'found' ? 'green' : 'orange'}>{v}</Tag>, width: 90 },
  ];

  return (
    <div>
      <Row align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <MobileOutlined style={{ marginRight: 8 }} />Handheld Scanner Simulator
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Simulate RFID handheld reader — login, select event, scan delegates</Text>
        </Col>
        {loggedIn && (
          <Col flex="1" style={{ textAlign: 'right' }}>
            <Space>
              <Tag color="green"><UserOutlined /> {sessionUser?.full_name}</Tag>
              <Button size="small" icon={<LogoutOutlined />} onClick={handleLogout} danger>Logout</Button>
            </Space>
          </Col>
        )}
      </Row>

      {/* Progress steps */}
      <Steps current={step} style={{ marginBottom: 24 }} size="small">
        <Step title="Login" icon={<LoginOutlined />} description="Operator login" />
        <Step title="Select Event" icon={<MobileOutlined />} description="Choose event & location" />
        <Step title="Scan" icon={<ScanOutlined />} description="RFID scan mode" />
      </Steps>

      <Row gutter={[24, 24]}>
        {/* Left panel */}
        <Col xs={24} lg={10}>
          {/* Step 0 – Login */}
          {step === 0 && (
            <Card title={<><LoginOutlined /> Operator Login</>} style={{ borderRadius: 12, border: '2px solid #0F1D6B' }}>
              <Alert message="Use your Identium system credentials to access the handheld scanner" type="info" showIcon style={{ marginBottom: 16 }} />
              <Form form={loginForm} layout="vertical">
                <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                  <Input prefix={<UserOutlined />} placeholder="admin" size="large" />
                </Form.Item>
                <Form.Item name="password" label="Password" rules={[{ required: true }]}>
                  <Input.Password placeholder="••••••••" size="large" />
                </Form.Item>
                <Button type="primary" size="large" block icon={<LoginOutlined />} onClick={handleLogin}
                  style={{ background: '#0F1D6B', marginTop: 8 }}>
                  Login to Handheld
                </Button>
              </Form>
            </Card>
          )}

          {/* Step 1 – Select Event */}
          {step === 1 && (
            <Card title={<><MobileOutlined /> Select Event & Location</>} style={{ borderRadius: 12, border: '2px solid #0F1D6B' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <div>
                  <Text strong>Select Event</Text>
                  <Select value={selectedEvent} onChange={loadLocations} style={{ width: '100%', marginTop: 6 }}
                    placeholder="Choose event" size="large">
                    {events.map(e => (
                      <Option key={e.id} value={e.id}>
                        <Badge color={e.status === 'active' ? 'green' : 'blue'} text={e.event_name} />
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text strong>Select Location / Zone</Text>
                  <Select value={selectedLocation} onChange={setSelectedLocation} style={{ width: '100%', marginTop: 6 }}
                    placeholder="Choose location" size="large" disabled={!selectedEvent}>
                    {locations.map(l => <Option key={l.id} value={l.id}>{l.location_name} ({l.location_code})</Option>)}
                  </Select>
                </div>
                <Button type="primary" size="large" block icon={<PlayCircleOutlined />} onClick={handleSelectEvent}
                  disabled={!selectedEvent || !selectedLocation} style={{ background: '#0F1D6B' }}>
                  Start Scanning
                </Button>
              </Space>
            </Card>
          )}

          {/* Step 2 – Scan */}
          {step === 2 && (
            <Card title={<><ScanOutlined /> RFID Scan Mode</>}
              style={{ borderRadius: 12, border: '2px solid #52c41a', background: '#f6ffed' }}
              extra={<Tag color="green">Active</Tag>}>

              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div>
                  <Text strong style={{ fontSize: 12 }}>Enter RFID Tag / Scan:</Text>
                  <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                    <Input
                      ref={tagRef}
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onPressEnter={() => performScan(tagInput)}
                      placeholder="Scan or type RFID tag..."
                      size="large"
                      prefix={<ScanOutlined style={{ color: '#52c41a' }} />}
                    />
                    <Button type="primary" size="large" onClick={() => performScan(tagInput)}
                      loading={scanning} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                      Scan
                    </Button>
                  </Space.Compact>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <Row align="middle" justify="space-between">
                  <Text strong>Auto-Scan Simulation</Text>
                  <Switch checkedChildren="ON" unCheckedChildren="OFF" checked={autoScan} onChange={setAutoScan} />
                </Row>
                {autoScan && <Alert message="Simulating RFID scans at 1.5s intervals using demo tags" type="warning" showIcon />}

                <Divider style={{ margin: '8px 0' }} />

                <Row gutter={16}>
                  <Col span={8}><Statistic title="Scanned" value={scanLog.length} /></Col>
                  <Col span={8}><Statistic title="Found" value={scanLog.filter(s => s.status === 'found').length} /></Col>
                  <Col span={8}><Statistic title="Unknown" value={scanLog.filter(s => s.status === 'unknown').length} /></Col>
                </Row>

                <Divider style={{ margin: '8px 0' }} />

                {syncDone
                  ? <Alert type="success" showIcon message={`${scanLog.length} scans synced to cloud`} />
                  : (
                    <Button block icon={<CloudSyncOutlined />} size="large" onClick={handleSync} loading={syncing}
                      style={{ background: '#0F1D6B', color: '#fff', border: 'none' }}>
                      Sync to Cloud ({scanLog.length} scans)
                    </Button>
                  )
                }

                <Card size="small" style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}>
                  <div style={{ fontSize: 12 }}><strong>Event:</strong> {events.find(e => e.id === selectedEvent)?.event_name}</div>
                  <div style={{ fontSize: 12 }}><strong>Location:</strong> {locations.find(l => l.id === selectedLocation)?.location_name}</div>
                </Card>
              </Space>
            </Card>
          )}
        </Col>

        {/* Right panel – Scan log */}
        <Col xs={24} lg={14}>
          <Card
            title={<><ScanOutlined /> Scan Log ({scanLog.length})</>}
            style={{ borderRadius: 12 }}
            extra={
              <Button size="small" icon={<ReloadOutlined />} onClick={() => setScanLog([])}>Clear</Button>
            }>
            {scanLog.length === 0
              ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
                  <ScanOutlined style={{ fontSize: 48, marginBottom: 12 }} />
                  <div>No scans yet — start scanning to see results here</div>
                </div>
              )
              : (
                <Table columns={scanColumns} dataSource={scanLog} rowKey="key"
                  size="small" pagination={{ pageSize: 20 }} />
              )
            }
          </Card>
        </Col>
      </Row>
    </div>
  );
}
